import React from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { SPRING_TRANSITION } from '../constants'
import type { Bounds } from '../types/types'

type FocusLayerProps = {
  isOpen: boolean
  initialBounds: Bounds | null
  finalBounds: Bounds // These bounds are expected to be viewport-relative
  onClose: () => void
  stageWidth: number
  stageHeight: number
  children: React.ReactNode
}

const FocusLayer: React.FC<FocusLayerProps> = ({
  isOpen,
  initialBounds,
  finalBounds,
  onClose,
  stageWidth,
  stageHeight,
  children,
}) => {
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  }

  // contentVariants now expect viewport-relative bounds directly
  const contentVariants = {
    hidden: (customInitialBounds: Bounds) => ({
      x: customInitialBounds.x,
      y: customInitialBounds.y,
      width: customInitialBounds.width,
      height: customInitialBounds.height,
      opacity: 0,
    }),
    visible: (customFinalBounds: Bounds) => ({
      x: customFinalBounds.x,
      y: customFinalBounds.y,
      width: customFinalBounds.width,
      height: customFinalBounds.height,
      opacity: 1,
      transition: SPRING_TRANSITION,
    }),
    exit: (customInitialBounds: Bounds) => ({
      x: customInitialBounds.x,
      y: customInitialBounds.y,
      width: customInitialBounds.width,
      height: customInitialBounds.height,
      opacity: 0,
      transition: SPRING_TRANSITION,
    }),
  }

  // defaultInitialBounds should also be viewport-relative if initialBounds is null
  const defaultInitialBounds: Bounds = initialBounds || {
    x: finalBounds.x + finalBounds.width / 2,
    y: finalBounds.y + finalBounds.height / 2,
    width: 0,
    height: 0,
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div // Outer container for absolute positioning within the timeline
          className="focus-layer"
          key="focus-layer-parent"
          style={{
            width: stageWidth,
            height: stageHeight,
            zIndex: 2000, // Ensure it's above other timeline items
          }}
          onClick={onClose}
          // This outer div does not animate opacity itself; AnimatePresence handles its appearance.
        >
          <motion.div
            key="focus-backdrop"
            className="focus-layer-backdrop"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={backdropVariants}
            transition={{ duration: 0.3 }}
          />
          <motion.div
            key="focus-content"
            className="focus-layer-content"
            style={{
              zIndex: 1, // Above backdrop within this parent
              // x, y, width, height will be set by motion
            }}
            custom={defaultInitialBounds} // For hidden and exit states (expects timeline-relative bounds)
            variants={contentVariants} // Variants expect timeline-relative bounds
            initial="hidden"
            animate={{
              ...contentVariants.visible(finalBounds), // finalBounds is timeline-relative
            }}
            exit="exit"
            onClick={(e: React.MouseEvent) => e.stopPropagation()} // Prevent clicks on content from closing
          >
            <div className="focus-layer-content__inner">
              <div className="focus-layer-content__inner-scrollable">{children}</div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default FocusLayer
